<template>
  <div
    class="edge-dock"
    :class="{ 'is-expanded': isExpanded }"
    role="toolbar"
    aria-label="Quick tools"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleDockClick"
  >
    <button
      v-for="item in items"
      :key="item.key"
      class="edge-dock-item"
      type="button"
      :aria-label="item.label"
      @click.stop="handleItemClick(item)"
    >
      <span class="edge-dock-icon">
        <component
          v-if="isComponentIcon(item.icon)"
          :is="item.icon"
          class="edge-dock-icon-svg"
          aria-hidden="true"
        />
        <i
          v-else-if="isIconClass(item.icon)"
          :class="item.icon"
          aria-hidden="true"
        />
        <img v-else :src="item.icon" :alt="item.label" />
        <span
          v-if="item.key === 'customer-service' && customerLoading"
          class="edge-dock-loading"
          aria-hidden="true"
        ></span>
        <span
          v-if="shouldShowBadge(item.badge)"
          class="edge-dock-badge"
          :class="{ 'is-dot': isBadgeDot(item.badge) }"
        >
          <slot name="badge" :item="item" :badge="item.badge">
            <span v-if="!isBadgeDot(item.badge)">{{
              formatBadge(item.badge)
            }}</span>
          </slot>
        </span>
      </span>
      <span class="edge-dock-label">{{ item.label }}</span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import YSF from '@neysf/qiyu-web-sdk'
import userService from '@/services/user'
import { getToken } from '@/utils/auth'
import useCommon from '@/composables/useCommon'
import { useRoute } from 'vue-router'

type BadgeValue = boolean | number | string | undefined | null;
type EdgeDockItem = {
  key: string;
  label: string;
  icon: any;
  onClick: () => void | Promise<void>;
  badge?: BadgeValue;
};

const collapsedWidth = 52;
const expandedWidth = 170;
const hoverOpenDelay = 240;
const hoverCloseDelay = 300;
const autoCollapseDelay = 10000;

const { t } = useI18n()
const store = useStore()
const { JumpToHref } = useCommon()
const { getUserInfo } = userService
const route = useRoute()
const unreadCount = ref(0)
const customerLoading = ref(false)
const isExpanded = ref(false)
const supportsHover = ref(false)

let hoverTimer: number | null = null;
let autoCollapseTimer: number | null = null;
let customerLoadingTimer: number | null = null;
let mediaQueryList: MediaQueryList | null = null;
let mediaQueryHandler: ((event: MediaQueryListEvent) => void) | null = null;

const {
  appContext: {
    config: { globalProperties },
  },
} = getCurrentInstance()!;

const lang = computed(() => store.state.user.language);

let customerInstance: any = null;

const items = computed<EdgeDockItem[]>(() => {
  lang.value;
  return [
    {
      key: "customer-service",
      label: `${t("msg.calculate.xskf")}`,
      icon: require("@/assets/images/icons/right_kf.png"),
      onClick: openCustomerService,
      badge: unreadCount.value > 0 ? unreadCount.value : undefined,
    },
    {
      key: "freight-calc",
      label: `${t("msg.other.yf_ss")}`,
      icon: require("@/assets/images/icons/right_jisq.png"),
      onClick: () => JumpToHref("Calculate"),
    },
    {
      key: "bookmark",
      label: `${t("msg.calculate.scwz")}`,
      icon: require("@/assets/images/icons/right_sc.png"),
      onClick: handleBookmark,
    },
    {
      key: "back-to-top",
      label: `${t("msg.calculate.top_back")}`,
      icon: require("@/assets/images/icons/right_top.png"),
      onClick: scrollToTop,
    },
  ];
});

const setBodyVars = () => {
  if (!document?.body) return;
  document.body.style.setProperty(
    "--edge-dock-expanded-width",
    `${expandedWidth}px`
  );
  document.body.style.setProperty(
    "--edge-dock-collapsed-width",
    `${collapsedWidth}px`
  );
};

const clearBodyVars = () => {
  if (!document?.body) return;
  document.body.style.removeProperty("--edge-dock-expanded-width");
  document.body.style.removeProperty("--edge-dock-collapsed-width");
};

const setExpandedState = (value: boolean) => {
  isExpanded.value = value;
};

const startAutoCollapse = () => {
  if (supportsHover.value) return;
  clearAutoCollapse();
  autoCollapseTimer = window.setTimeout(() => {
    setExpandedState(false);
  }, autoCollapseDelay);
};

const clearAutoCollapse = () => {
  if (autoCollapseTimer) {
    window.clearTimeout(autoCollapseTimer);
    autoCollapseTimer = null;
  }
};

const scheduleHover = (expand: boolean) => {
  if (!supportsHover.value) return;
  if (hoverTimer) {
    window.clearTimeout(hoverTimer);
    hoverTimer = null;
  }
  hoverTimer = window.setTimeout(
    () => {
      setExpandedState(expand);
    },
    expand ? hoverOpenDelay : hoverCloseDelay
  );
};

const handleMouseEnter = () => {
  scheduleHover(true);
};

const handleMouseLeave = () => {
  scheduleHover(false);
};

const handleDockClick = () => {
  if (supportsHover.value) return;
  if (isExpanded.value) {
    clearAutoCollapse();
    setExpandedState(false);
    return;
  }
  setExpandedState(true);
  startAutoCollapse();
};

const handleItemClick = async (item: EdgeDockItem) => {
  if (!supportsHover.value && !isExpanded.value) {
    setExpandedState(true);
  }
  try {
    await item.onClick();
  } finally {
    if (!supportsHover.value) {
      clearAutoCollapse();
      setExpandedState(false);
    }
  }
};

const isComponentIcon = (icon: any) =>
  typeof icon === "object" || typeof icon === "function";
const isIconClass = (icon: any) =>
  typeof icon === "string" &&
  !/(\.png|\.svg|\.jpg|\.jpeg|\.gif|\.webp)$/i.test(icon) &&
  !icon.startsWith("data:");

const isBadgeDot = (badge: BadgeValue) => badge === true;
const shouldShowBadge = (badge: BadgeValue) => {
  if (badge === true) return true;
  if (badge === false || badge === null || badge === undefined) return false;
  if (badge === 0 || badge === "0" || badge === "") return false;
  return true;
};
const formatBadge = (badge: BadgeValue) =>
  typeof badge === "number" || typeof badge === "string" ? String(badge) : "";

const handleBookmark = () => {
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") !== -1;
  const shortcut = isMac ? "Command + D" : "Ctrl + D";
  ElMessage({
    message: `请使用快捷键 ${shortcut} 将本页添加到书签`,
    type: "info",
    duration: 3000,
    showClose: true,
  });
};

const setCustomerLoading = (value: boolean) => {
  if (customerLoadingTimer) {
    window.clearTimeout(customerLoadingTimer);
    customerLoadingTimer = null;
  }
  customerLoading.value = value;
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

const initCustomerService = async () => {
  if (customerInstance) return customerInstance;
  try {
    const ysf = await YSF.init("45656fef3487f900cbe100fb04c8057c", {
      sdkTemplateId: 8128650,
      templateId: 8128650,
      sessionInvite: false,
      hidden: 1,
    });

    globalProperties.$customer = ysf;
    customerInstance = ysf;
    ysf("onunread", (res) => {
      unreadCount.value = res.total;
    });

    const token = store.state.user.token || getToken();
    if (token) {
      try {
        const cachedUser = store.state.user?.user
        const userInfo =
          cachedUser && cachedUser.user_id
            ? cachedUser
            : (await getUserInfo()).data?.result
        if (userInfo && (!cachedUser || !cachedUser.user_id)) {
          store.dispatch('user/setUserInfo', userInfo)
        }
        ysf('config', {
          uid: userInfo.user_id,
          nickname: `${userInfo.user_id}-买到手`,
          email: userInfo.email,
          mobile_phone: userInfo.mobile,
          data: JSON.stringify([
            { key: "uid", value: userInfo.user_id, label: "UserId" },
            {
              key: "nickname",
              value: `${userInfo.user_id}-买到手`,
              label: "昵称",
            },
            { key: "mobile_phone", value: userInfo.mobile, label: "手机号码" },
            { key: "email", value: userInfo.email, label: "邮箱" },
          ]),
          language: lang.value === "zh-CN" ? "zh-cn" : "zh-tw",
        });
      } catch (err) {
        console.error("获取用户信息失败", err);
      }
    } else {
      ysf("config", {
        language: lang.value === "zh-CN" ? "zh-cn" : "zh-tw",
      });
    }

    return ysf;
  } catch (error) {
    console.error("客服 SDK 初始化失败", error);
    throw error;
  }
};

async function openCustomerService() {
  setCustomerLoading(true);
  try {
    const ysf = await initCustomerService();
    ysf("open");
    unreadCount.value = 0;
  } catch (error) {
    console.error("打开客服失败", error);
  } finally {
    customerLoadingTimer = window.setTimeout(() => {
      setCustomerLoading(false);
    }, 900);
  }
}

const setupHoverMedia = () => {
  if (!window?.matchMedia) return;
  mediaQueryList = window.matchMedia("(hover: hover) and (pointer: fine)");
  supportsHover.value = mediaQueryList.matches;
  mediaQueryHandler = (event: MediaQueryListEvent) => {
    supportsHover.value = event.matches;
    if (!supportsHover.value && isExpanded.value) {
      startAutoCollapse();
    } else {
      clearAutoCollapse();
    }
  };
  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener("change", mediaQueryHandler);
  } else {
    mediaQueryList.addListener(mediaQueryHandler);
  }
};

const teardownHoverMedia = () => {
  if (!mediaQueryList || !mediaQueryHandler) return;
  if (mediaQueryList.removeEventListener) {
    mediaQueryList.removeEventListener("change", mediaQueryHandler);
  } else {
    mediaQueryList.removeListener(mediaQueryHandler);
  }
  mediaQueryList = null;
  mediaQueryHandler = null;
};

onMounted(() => {
  setBodyVars()
  setupHoverMedia()
  if (route.name !== 'Category') {
    initCustomerService().catch(() => {
      // Fallback: re-init on click if the eager init fails.
    })
  }
})
watch(
  () => route.name,
  (name) => {
    if (name === 'Category') return
    initCustomerService().catch(() => {
      // Fallback: re-init on click if the eager init fails.
    })
  }
)

onBeforeUnmount(() => {
  if (hoverTimer) {
    window.clearTimeout(hoverTimer);
    hoverTimer = null;
  }
  if (customerLoadingTimer) {
    window.clearTimeout(customerLoadingTimer);
    customerLoadingTimer = null;
  }
  clearAutoCollapse();
  teardownHoverMedia();
  setExpandedState(false);
  clearBodyVars();
});
</script>

<style lang="scss" scoped>
.edge-dock {
  position: fixed;
  right: 0;
  top: 50%;
  width: var(--edge-dock-expanded-width, 170px);
  transform: translate3d(
    calc(
      var(--edge-dock-expanded-width, 170px) -
        var(--edge-dock-collapsed-width, 52px)
    ),
    -50%,
    0
  );
  transition: transform 0.28s ease;
  will-change: transform;
  z-index: 98;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 8px;
  background: #fdfaf1;
  border-radius: 14px 0 0 14px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.14);
}

.edge-dock.is-expanded {
  transform: translate3d(0, -50%, 0);
}

.edge-dock-item {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 8px 6px;
  border-radius: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
  cursor: pointer;
  color: #333333;
}

.edge-dock-icon {
  position: relative;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.edge-dock-icon-svg {
  width: 100%;
  height: 100%;
}

.edge-dock-label {
  font-size: 12px;
  line-height: 1.2;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  max-width: 120px;
  opacity: 0;
  transform: translateX(4px);
  transition: opacity 0.2s ease, transform 0.2s ease, max-width 0.2s ease;
}

.edge-dock.is-expanded .edge-dock-label {
  opacity: 1;
  transform: translateX(0);
}

.edge-dock:not(.is-expanded) .edge-dock-item {
  justify-content: flex-start;
}

.edge-dock:not(.is-expanded) .edge-dock-label {
  max-width: 0;
}

.edge-dock-badge {
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #ff4d4f;
  color: #ffffff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  padding: 0 5px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.edge-dock-badge.is-dot {
  min-width: 8px;
  height: 8px;
  padding: 0;
}

.edge-dock-loading {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid rgba(255, 205, 1, 0.25);
  border-top-color: #ffcd01;
  animation: edge-dock-spin 0.8s linear infinite;
  pointer-events: none;
}

@keyframes edge-dock-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (hover: hover) and (pointer: fine) {
  .edge-dock-item:hover {
    background: rgba(0, 0, 0, 0.05);
  }
}
</style>
